import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import formatDateTime from "components/Shared/formatDateTime"
import { getMenuSetup } from "store/actions/menuSetupAction"
import { getAllBranchs } from "store/actions/usersAction"
import { getBrandsFromCommonData } from "store/actions/commonAction"
import { useBrandOptions } from "hooks"
import { getSubPermissions } from "common/isPermissionEnabled"
// import { getBrandList } from "store/actions/brandsAction"

const ViewMenuSetup = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Menu Setup Details ")

  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const menuId = queryParams.get("id")
  const { allowedBrands } = useBrandOptions()

  const { isLoading, singleMenuSetup } = useSelector(state => state.MenuSetup)
  const { userRole, accountType } = useSelector(state => state.Login)

  const canViewPosMenuFields = useMemo(() => {
    const addSubs = getSubPermissions(userRole, 4, "menus_2", accountType) || {}
    const updateSubs =
      getSubPermissions(userRole, 4, "menus_3", accountType) || {}
    return (
      addSubs.link_menu_with_pos?.isEnabled === true ||
      updateSubs.link_menu_with_pos?.isEnabled === true
    )
  }, [userRole, accountType])

  const { branchsList } = useSelector(state => state.Users)

  // const { brandList } = useSelector(state => state.Brands)

  useEffect(() => {
    dispatch(getAllBranchs())
    dispatch(getBrandsFromCommonData())
    // fetchBrands()

    if (menuId) {
      dispatch(getMenuSetup(menuId))
    }
  }, [menuId])

  // const fetchBrands = () => {
  //   dispatch(getBrandList())
  // }

  const renderCountryNames = countriesList => {
    if (
      !countriesList ||
      (Array.isArray(countriesList) && countriesList.length === 0)
    ) {
      return "-"
    }

    // Handle both array of objects and array of strings just in case
    const list = Array.isArray(countriesList) ? countriesList : [countriesList]

    const names = list
      .map(country => {
        if (!country) return null
        if (typeof country === "string") return country
        return language === "sa"
          ? country.nameAr || country.name || country.countryCode
          : country.name || country.nameAr || country.countryCode
      })
      .filter(Boolean)

    return names.length ? names.join(", ") : "-"
  }

  const brandOptions =
    allowedBrands?.map(obj => ({
      value: obj._id,
      label: obj.brandName,
    })) || []

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {isLoading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={12}>
                  <ShimmerThumbnail height={600} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 d-flex align-items-center">
                    <BackButton /> {t("Menu Setup Details")}
                  </CardTitle>
                  <div className="table-responsive">
                    {!singleMenuSetup ? (
                      <p className="text-center">
                        {t("No menu details found")}
                      </p>
                    ) : (
                      <Table className="table mb-0">
                        <tbody>
                          {canViewPosMenuFields && (
                            <tr>
                              <th scope="row">{t("POS ID")}</th>
                              <td>{singleMenuSetup.menuId || "-"}</td>
                            </tr>
                          )}
                          {canViewPosMenuFields && (
                            <tr>
                              <th scope="row">{t("App Menu ID")}</th>
                              <td>{singleMenuSetup.appMenuId || "-"}</td>
                            </tr>
                          )}
                          <tr>
                            <th scope="row">{t("Name (English)")}</th>
                            <td className="text-wrap">
                              {singleMenuSetup.menuNameEn || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Name (عربي)")}</th>
                            <td className="text-wrap text-start">
                              {singleMenuSetup.menuNameAr || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Description (English)")}</th>
                            <td className="text-wrap">
                              {singleMenuSetup.menuDescriptionEn || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Description (عربي)")}</th>
                            <td className="text-wrap text-start">
                              {singleMenuSetup.menuDescriptionAr || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Country")}</th>
                            <td>
                              {renderCountryNames(singleMenuSetup?.countries)}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Brand")}</th>
                            <td>
                              {brandOptions?.find(
                                option =>
                                  option?.value === singleMenuSetup?.brand
                              )?.label || "-"}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("Branches")}</th>
                            <td>
                              {Array.isArray(singleMenuSetup.branches) &&
                              singleMenuSetup.branches.length > 0 &&
                              Array.isArray(branchsList)
                                ? singleMenuSetup.branches
                                    .map(branchId => {
                                      const branch = branchsList.find(
                                        b => b._id === branchId
                                      )
                                      return branch ? branch.name : null
                                    })
                                    .filter(Boolean) // remove nulls
                                    .join(", ")
                                : "-"}
                            </td>
                          </tr> */}

                          <tr>
                            <th scope="row">{t("Is Default Menu")}</th>
                            <td>
                              {singleMenuSetup.isDefaultMenu
                                ? t("Yes")
                                : t("No")}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Status")}</th>
                            <td>
                              {singleMenuSetup.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Inactive")}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Created At")}</th>
                            <td>
                              {formatDateTime(singleMenuSetup.createdAt) || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Updated At")}</th>
                            <td>
                              {formatDateTime(singleMenuSetup.updatedAt) || "-"}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default ViewMenuSetup
